import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';

import { fetchAreaNetworkStats, fetchDmaNetworkStats, type NetworkStats, type StatsPeriod } from '@/api/statsApi';
import { statsScopeKey, type StatsScope } from '@/components/map/utils/resolveStatsScope';
import { useClient } from '@/clients/useClient';
import { useStatsFilters } from '@/stats/StatsFiltersContext';

/** Exported for test isolation only — do not call in production code. */
export function _clearScopedStatsCache() {
    scopedStatsCache.clear();
}

// Module-level cache keyed by `clientId:scopeKey:period`, mirroring useNetworkStats.
// Panning back into a recently visited area/DMA re-shows its stats without a refetch.
// 404s are deliberately not cached — a missing polygon may exist on a later pass.
const scopedStatsCache = new Map<string, NetworkStats>();

export interface ScopedStatsState {
    /** Stats for `dataScope`, or null when idle / not found / errored. Stale-kept while loading. */
    data: NetworkStats | null;
    /** The scope `data` belongs to — moves in lockstep with `data` so the helper line never mixes scopes. */
    dataScope: StatsScope | null;
    isLoading: boolean;
    isError: boolean;
}

const IDLE_STATE: ScopedStatsState = { data: null, dataScope: null, isLoading: false, isError: false };

/**
 * Fetches network-stats-shaped totals for the current stats scope (area/DMA under
 * the map centre). The network scope is idle by design — network numbers keep
 * coming from `useNetworkStats`, so network-level behaviour cannot regress.
 *
 * A 404 (scope unknown to the backend) resolves to empty data with no error, so
 * consumers silently fall back to the network view. While a new scope is loading,
 * the previous scope's data/dataScope pair is kept to avoid flicker — except
 * across client changes, where stale data would belong to another client.
 */
export function useScopedStats(scope: StatsScope, period: StatsPeriod): ScopedStatsState {
    const { selectedClient } = useClient();
    const clientId = selectedClient?.id ?? null;
    // The committed map filters, bridged up from MapView — scoped totals must describe
    // the same filtered population the map and the network figures do.
    const { filters, filtersKey } = useStatsFilters();

    const scopeKey = statsScopeKey(scope);
    const fetchKey = clientId && scope.type !== 'network' ? `${clientId}:${scopeKey}:${period}:${filtersKey}` : null;

    // The fetch effect keys on primitives (fetchKey et al.) but needs the full scope
    // object to tag results with; read it through a ref so a caller passing a fresh
    // (but key-identical) object every render cannot re-trigger the effect.
    const scopeRef = useRef(scope);
    useLayoutEffect(() => {
        scopeRef.current = scope;
    });

    // The last fetch result, tagged with the client it belongs to so a client
    // switch implicitly discards it (stale-keeping across clients would show
    // another client's numbers).
    const [result, setResult] = useState<{ clientId: string; data: NetworkStats; dataScope: StatsScope } | null>(null);

    // Tracks which fetchKey has finished (success, 404 or error) so isLoading can be
    // derived without synchronous setState in the effect body (react-hooks/set-state-in-effect).
    const [completedFor, setCompletedFor] = useState<string | null>(() =>
        fetchKey && scopedStatsCache.has(fetchKey) ? fetchKey : null,
    );
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        if (!fetchKey || !clientId) return;
        if (scopedStatsCache.has(fetchKey)) return;

        const scopeForThisFetch = scopeRef.current;
        const controller = new AbortController();

        const request =
            scopeForThisFetch.type === 'dma'
                ? fetchDmaNetworkStats(clientId, scopeForThisFetch.ident, period, filters, controller.signal)
                : fetchAreaNetworkStats(
                      clientId,
                      scopeForThisFetch.type === 'area' ? scopeForThisFetch.name : '',
                      period,
                      filters,
                      controller.signal,
                  );

        request
            .then((stats) => {
                scopedStatsCache.set(fetchKey, stats);
                setResult({ clientId, data: stats, dataScope: scopeForThisFetch });
                setIsError(false);
                setCompletedFor(fetchKey);
            })
            .catch((err: unknown) => {
                if (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError')) return;
                // Scope unknown to the backend (e.g. polygon without stats): fall back to
                // the network view silently — no toast, no error state, no cache entry.
                const notFound = isAxiosError(err) && err.response?.status === 404;
                setResult(null);
                setIsError(!notFound);
                setCompletedFor(fetchKey);
            });

        return () => {
            controller.abort();
        };
        // `filters` identity changes exactly when filtersKey (inside fetchKey) does — the
        // provider bails out on key-identical publishes — and a cache hit returns early, so
        // listing it cannot cause redundant fetches.
    }, [fetchKey, clientId, period, filters]);

    if (scope.type === 'network' || !clientId || !fetchKey) return IDLE_STATE;

    const cached = scopedStatsCache.get(fetchKey);
    const isLoading = !cached && completedFor !== fetchKey;

    if (cached) return { data: cached, dataScope: scope, isLoading: false, isError: false };
    if (result && result.clientId === clientId) {
        // Stale-while-loading: previous scope's stats stay up until the new response lands.
        return { data: result.data, dataScope: result.dataScope, isLoading, isError };
    }
    return { data: null, dataScope: null, isLoading, isError };
}
